/**
 * SpendNova attribution capture
 *
 * Captures traffic source parameters from any entry URL and persists them
 * for the entire browsing session so they can be:
 *   1. Forwarded with the waitlist POST body to Bill Ready One's /api/waitlist
 *   2. Appended to outbound Install-page CTAs (TestFlight / APK) so
 *      downstream analytics can correlate downloads to the source campaign.
 *
 * Supports BOTH short `?ref=tiktok` style and full UTM params.
 */

const ATTR_KEYS = [
  "ref",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const STORAGE_KEY = "sn_attribution";
const SESSION_ID_KEY = "sn_session_id";

const safeGetSession = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  return window.sessionStorage;
};

const genSessionId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {}
  return `sn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const ensureSessionId = () => {
  const s = safeGetSession();
  if (!s) return null;
  let id = s.getItem(SESSION_ID_KEY);
  if (!id) {
    id = genSessionId();
    try { s.setItem(SESSION_ID_KEY, id); } catch (_) {}
  }
  return id;
};

/**
 * Read attribution params from the current URL and persist them.
 * First-touch model: never overwrites already-stored values within a session,
 * so if a tester lands via ?ref=tiktok then clicks around the site, tiktok wins.
 */
export const captureAttribution = () => {
  const s = safeGetSession();
  if (!s || typeof window === "undefined") return {};
  ensureSessionId();

  let stored = {};
  try {
    stored = JSON.parse(s.getItem(STORAGE_KEY) || "{}");
  } catch (_) {
    stored = {};
  }

  const params = new URLSearchParams(window.location.search);
  let changed = false;
  ATTR_KEYS.forEach((k) => {
    const incoming = params.get(k);
    if (incoming && !stored[k]) {
      stored[k] = String(incoming).slice(0, 128);
      changed = true;
    }
  });

  if (changed) {
    stored.captured_at = stored.captured_at || new Date().toISOString();
    stored.landing_url =
      stored.landing_url || String(window.location.href).slice(0, 512);
    try {
      s.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (_) {}
  }

  return stored;
};

/** Read whatever attribution we've already captured (never triggers a URL read). */
export const getAttribution = () => {
  const s = safeGetSession();
  if (!s) return {};
  try {
    return JSON.parse(s.getItem(STORAGE_KEY) || "{}");
  } catch (_) {
    return {};
  }
};

/** Build a plain payload of just the traffic keys (no captured_at/landing_url) — for API POST bodies. */
export const attributionPayload = () => {
  const attr = getAttribution();
  const out = {};
  ATTR_KEYS.forEach((k) => {
    if (attr[k]) out[k] = attr[k];
  });
  if (attr.landing_url) out.landing_url = attr.landing_url;
  if (attr.captured_at) out.captured_at = attr.captured_at;
  const sid = ensureSessionId();
  if (sid) out.session_id = sid;
  return out;
};

/** Given an outbound URL, append currently-stored attribution params (without overwriting existing ones). */
export const withAttribution = (url) => {
  if (!url) return url;
  const attr = getAttribution();
  const relevant = ATTR_KEYS.filter((k) => attr[k]);
  if (relevant.length === 0) return url;
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.href : undefined);
    relevant.forEach((k) => {
      if (!u.searchParams.has(k)) u.searchParams.set(k, attr[k]);
    });
    return u.toString();
  } catch (_) {
    return url;
  }
};

export { ATTR_KEYS };
